// src/components/modals/QuickSaleModal.tsx
// Vendita al banco segnata dall'agenda: cliente, uno o più prodotti, e —
// se chi opera è il titolare — l'operatore a cui accreditarla.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search, ShoppingBag, Loader2, Plus, Minus, X, User, Check } from 'lucide-react';

import Modal from '../ui/Modal';
import CustomSelect from '../ui/CustomSelect';
import { useInventory } from '@/contexts/InventoryContext';
import { useClients } from '@/contexts/ClientsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAuth } from '@/contexts/AuthContext';

interface QuickSaleModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** cliente già scelto (es. vendita partita da un appuntamento) */
    presetClientId?: string | null;
    presetHairdresserId?: string | null;
}

const QuickSaleModal: React.FC<QuickSaleModalProps> = ({
    isOpen,
    onClose,
    presetClientId = null,
    presetHairdresserId = null,
}) => {
    const { t } = useTranslation();
    const { products, sell } = useInventory();
    const { clients } = useClients();
    const { users } = useUsers();
    const { user, isAdmin } = useAuth();

    const [clientSearch, setClientSearch] = useState('');
    const [clientId, setClientId] = useState<string | null>(presetClientId);
    const [productSearch, setProductSearch] = useState('');
    const [lines, setLines] = useState<{ product_id: number; quantity: number }[]>([]);
    const [hairdresserId, setHairdresserId] = useState<string | null>(
        presetHairdresserId || user?.id || null
    );
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setClientId(presetClientId);
            setHairdresserId(presetHairdresserId || user?.id || null);
            setLines([]);
            setClientSearch('');
            setProductSearch('');
        }
    }, [isOpen, presetClientId, presetHairdresserId, user?.id]);

    const selectedClient = useMemo(
        () => (clients || []).find((c: any) => String(c.id) === String(clientId)),
        [clients, clientId]
    );

    const clientResults = useMemo(() => {
        const term = clientSearch.trim().toLowerCase();
        if (!term) return [];
        return (clients || [])
            .filter((c: any) => c.is_active !== false)
            .filter(
                (c: any) =>
                    (c.full_name || '').toLowerCase().includes(term) ||
                    (c.phone || '').includes(term) ||
                    (c.email || '').toLowerCase().includes(term)
            )
            .slice(0, 6);
    }, [clients, clientSearch]);

    const productResults = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        const available = products.filter((p) => p.stock > 0);
        if (!term) return available.slice(0, 6);
        return available.filter((p) => p.name.toLowerCase().includes(term)).slice(0, 6);
    }, [products, productSearch]);

    const total = useMemo(
        () =>
            lines.reduce((sum, l) => {
                const p = products.find((prod) => prod.id === l.product_id);
                return sum + Number(p?.price ?? 0) * l.quantity;
            }, 0),
        [lines, products]
    );

    const addLine = (productId: number) => {
        setLines((prev) => {
            const existing = prev.find((l) => l.product_id === productId);
            if (existing) {
                return prev.map((l) =>
                    l.product_id === productId ? { ...l, quantity: l.quantity + 1 } : l
                );
            }
            return [...prev, { product_id: productId, quantity: 1 }];
        });
        setProductSearch('');
    };

    const changeQty = (productId: number, delta: number) => {
        setLines((prev) =>
            prev
                .map((l) => (l.product_id === productId ? { ...l, quantity: l.quantity + delta } : l))
                .filter((l) => l.quantity > 0)
        );
    };

    const staffOptions = useMemo(
        () =>
            (users || [])
                .filter((u: any) => u.is_active !== false)
                .map((u: any) => ({
                    value: String(u.id),
                    label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
                })),
        [users]
    );

    const handleConfirm = async () => {
        if (!lines.length) return;

        const overStock = lines.find((l) => {
            const p = products.find((prod) => prod.id === l.product_id);
            return !p || l.quantity > p.stock;
        });
        if (overStock) {
            toast.error(t('inventory.not_enough_stock', 'Giacenza insufficiente'));
            return;
        }

        setIsSaving(true);
        const res = await sell({
            client_id: clientId,
            hairdresser_id: hairdresserId,
            lines: lines.map((l) => ({
                product_id: l.product_id,
                quantity: l.quantity,
                unit_price: Number(products.find((p) => p.id === l.product_id)?.price ?? 0),
            })),
        });
        setIsSaving(false);

        if (res.success) {
            toast.success(t('inventory.sale_saved', 'Vendita registrata'));
            onClose();
        } else {
            toast.error(res.error || t('common.error'));
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            compact
            className="max-w-md"
            headerClassName="bg-white"
            closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
            headerContent={
                <div className="pr-10">
                    <p className="text-[11px] font-bold text-clip-500 uppercase tracking-wider">
                        {t('inventory.sale.eyebrow', 'Magazzino')}
                    </p>
                    <h2 className="text-xl font-bold text-gray-900">
                        {t('inventory.sale.title', 'Segna una vendita')}
                    </h2>
                </div>
            }
        >
            <div className="space-y-5">
                {/* CLIENTE */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                        {t('inventory.sale.client', 'Cliente')}
                    </label>

                    {selectedClient ? (
                        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                            <div className="w-9 h-9 rounded-full bg-clip-50 border border-clip-100 flex items-center justify-center text-xs font-bold uppercase text-clip-600">
                                {(selectedClient.full_name || '?')
                                    .split(' ')
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map((w: string) => w[0])
                                    .join('')}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-gray-900 truncate">{selectedClient.full_name}</p>
                                <p className="text-xs text-gray-500 truncate">
                                    {selectedClient.phone || selectedClient.email || ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setClientId(null)}
                                className="text-gray-400 hover:text-gray-700"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                    placeholder={t('inventory.sale.client_placeholder', 'Cerca cliente (facoltativo)') as string}
                                    className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none"
                                />
                            </div>
                            {clientResults.length > 0 && (
                                <div className="mt-2 rounded-xl border border-gray-200 overflow-hidden">
                                    {clientResults.map((c: any, i: number) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => {
                                                setClientId(String(c.id));
                                                setClientSearch('');
                                            }}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${i > 0 ? 'border-t border-gray-100' : ''
                                                }`}
                                        >
                                            <User size={14} className="text-gray-400 flex-shrink-0" />
                                            <span className="font-semibold text-gray-900 truncate">{c.full_name}</span>
                                            <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{c.phone}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* PRODOTTI */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                        {t('inventory.sale.products', 'Prodotti venduti')}
                    </label>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            placeholder={t('inventory.search_placeholder', 'Cerca prodotto...') as string}
                            className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none"
                        />
                    </div>

                    {productResults.length > 0 && (
                        <div className="mt-2 rounded-xl border border-gray-200 overflow-hidden max-h-52 overflow-y-auto">
                            {productResults.map((p, i) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => addLine(p.id)}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${i > 0 ? 'border-t border-gray-100' : ''
                                        }`}
                                >
                                    <Plus size={14} className="text-clip-500 flex-shrink-0" />
                                    <span className="font-semibold text-gray-900 truncate">{p.name}</span>
                                    <span className="text-xs text-gray-400 ml-auto flex-shrink-0 tabular-nums">
                                        €{Number(p.price).toFixed(2)} · {p.stock} {t('inventory.pieces_short', 'pz')}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {products.length === 0 && (
                        <p className="mt-2 text-xs text-gray-400">
                            {t('inventory.sale.no_products', 'Nessun prodotto a magazzino: falli entrare dalla sezione Magazzino.')}
                        </p>
                    )}

                    {lines.length > 0 && (
                        <ul className="mt-3 space-y-2">
                            {lines.map((l) => {
                                const p = products.find((prod) => prod.id === l.product_id);
                                if (!p) return null;
                                return (
                                    <li
                                        key={l.product_id}
                                        className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                                            <p className="text-xs text-gray-500 tabular-nums">
                                                €{Number(p.price).toFixed(2)} × {l.quantity} = €
                                                {(Number(p.price) * l.quantity).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => changeQty(l.product_id, -1)}
                                                className="px-2 py-1.5 text-gray-500 hover:bg-gray-50"
                                            >
                                                <Minus size={12} />
                                            </button>
                                            <span className="w-7 text-center text-xs font-bold tabular-nums">
                                                {l.quantity}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => changeQty(l.product_id, 1)}
                                                disabled={l.quantity >= p.stock}
                                                className="px-2 py-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                                            >
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* OPERATORE: solo il titolare può accreditare la vendita a un altro */}
                {isAdmin && (
                    <div className="relative z-30">
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                            {t('inventory.sale.staff', 'Vendita di')}
                        </label>
                        <CustomSelect
                            value={hairdresserId || ''}
                            onChange={(v) => setHairdresserId(v)}
                            options={staffOptions}
                            placeholder={t('inventory.sale.staff_placeholder', 'Scegli operatore') as string}
                        />
                    </div>
                )}

                {/* TOTALE + CONFERMA */}
                <div className="pt-4 border-t border-gray-100 flex items-center gap-3">
                    <div className="flex-shrink-0">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                            {t('inventory.sale.total', 'Totale')}
                        </p>
                        <p className="text-xl font-bold text-gray-900 tabular-nums">€{total.toFixed(2)}</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSaving || lines.length === 0}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 hover:bg-black text-white font-bold transition-colors disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {t('inventory.sale.confirm', 'Registra vendita')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default QuickSaleModal;
